function hexToRgba(hex, opacity = 1) {
  if (!hex?.startsWith('#')) return hex || 'rgba(15,15,30,0.9)';
  const h = hex.replace('#', '');
  const r = Number.parseInt(h.slice(0, 2), 16);
  const g = Number.parseInt(h.slice(2, 4), 16);
  const b = Number.parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${opacity})`;
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function renderFormFieldHtml(field, style) {
  const textColor = style?.textColor || '#ffffff';
  const accentColor = style?.accentColor || 'var(--brand-primary)';
  const buttonColor = style?.buttonColor || 'var(--brand-primary)';

  const inputCss = [
    'width:100%',
    'padding:7px 10px',
    'background:rgba(255,255,255,0.08)',
    'border:1px solid rgba(255,255,255,0.18)',
    'border-radius:7px',
    `color:${textColor}`,
    'font-size:12px',
    'box-sizing:border-box',
    'outline:none',
  ].join(';');

  const labelCss = `font-size:10px;color:rgba(255,255,255,0.55);display:block;margin-bottom:3px;font-weight:600;`;
  const reqSpan = field.required ? `<span style="color:${accentColor}"> *</span>` : '';

  if (field.type === 'submit') {
    return `<input type="submit" value="${escapeHtml(field.label || 'Submit')}" `
      + `style="width:100%;padding:9px 12px;background:${buttonColor};color:#fff;border:none;`
      + `border-radius:50px;font-size:13px;font-weight:700;cursor:pointer;">`;
  }

  if (field.type === 'textarea') {
    const label = field.label
      ? `<label style="${labelCss}">${escapeHtml(field.label)}${reqSpan}</label>`
      : '';
    return `<div>${label}<textarea name="${escapeHtml(field.name)}" `
      + `placeholder="${escapeHtml(field.placeholder || '')}"${field.required ? ' required' : ''} `
      + `style="${inputCss};resize:none;height:60px;font-family:inherit;"></textarea></div>`;
  }

  if (field.type === 'select') {
    const label = field.label
      ? `<label style="${labelCss}">${escapeHtml(field.label)}${reqSpan}</label>`
      : '';
    const opts = (field.options || [])
      .map(o => `<option value="${escapeHtml(o.value)}">${escapeHtml(o.label)}</option>`)
      .join('');
    return `<div>${label}<select name="${escapeHtml(field.name)}"${field.required ? ' required' : ''} `
      + `style="${inputCss}">`
      + `<option value="">${escapeHtml(field.placeholder || 'Select an option')}</option>${opts}`
      + `</select></div>`;
  }

  if (field.type === 'checkbox') {
    return `<label style="display:flex;align-items:center;gap:8px;cursor:pointer;">`
      + `<input type="checkbox" name="${escapeHtml(field.name)}"${field.required ? ' required' : ''}> `
      + `<span style="font-size:12px;color:${textColor};">${escapeHtml(field.label || 'Checkbox')}</span>`
      + `</label>`;
  }

  if (field.type === 'radio') {
    const label = field.label
      ? `<div style="${labelCss}">${escapeHtml(field.label)}${reqSpan}</div>`
      : '';
    const options = field.options?.length
      ? field.options
      : [{ label: 'Option 1', value: 'opt1' }, { label: 'Option 2', value: 'opt2' }];
    const radios = options.map(o =>
      `<label style="display:flex;align-items:center;gap:7px;cursor:pointer;">`
      + `<input type="radio" name="${escapeHtml(field.name)}" value="${escapeHtml(o.value)}"> `
      + `<span style="font-size:12px;color:${textColor};">${escapeHtml(o.label)}</span>`
      + `</label>`
    ).join('');
    return `<div>${label}<div style="display:flex;flex-direction:column;gap:5px;">${radios}</div></div>`;
  }

  // text | email | phone | number | date | url
  const htmlType = field.type === 'phone' ? 'tel' : field.type;
  const label = field.label
    ? `<label style="${labelCss}">${escapeHtml(field.label)}${reqSpan}</label>`
    : '';
  return `<div>${label}<input type="${htmlType}" name="${escapeHtml(field.name)}" `
    + `placeholder="${escapeHtml(field.placeholder || '')}"${field.required ? ' required' : ''} `
    + `style="${inputCss}"></div>`;
}

function generateFormLayerHtml(layer) {
  const t = layer.transform || {};
  const d = layer.data || {};
  const s = d.style || {};
  const fields = d.fields || [];

  const rawBg = s.backgroundColor || '#0f0f1e';
  const bgOpacity = (s.backgroundOpacity ?? 90) / 100;
  const bg = hexToRgba(rawBg, bgOpacity);
  const textColor = s.textColor || '#ffffff';
  const borderRadius = s.borderRadius ?? 12;

  const wrapperCss = [
    'position:absolute',
    `left:${t.x ?? 10}%`,
    `top:${t.y ?? 10}%`,
    `width:${t.width ?? 80}%`,
    `height:${t.height ?? 40}%`,
    `opacity:${t.opacity ?? 1}`,
    `z-index:${t.zIndex ?? 1}`,
    t.rotation ? `transform:rotate(${t.rotation}deg)` : '',
  ].filter(Boolean).join(';');

  const formCss = [
    'width:100%',
    'height:100%',
    `background:${bg}`,
    `border-radius:${borderRadius}px`,
    'padding:14px',
    'display:flex',
    'flex-direction:column',
    'gap:9px',
    'overflow:hidden',
    'box-sizing:border-box',
    'border:1px solid rgba(255,255,255,0.08)',
  ].join(';');

  const titleHtml = d.formTitle
    ? `<div style="font-size:12px;font-weight:700;color:${textColor};`
      + `overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">`
      + `${escapeHtml(d.formTitle)}</div>`
    : '';

  const visibleFields = fields.filter(f => f.type !== 'hidden');
  const hiddenFields = fields.filter(f => f.type === 'hidden');

  const fieldsHtml = visibleFields.map(f => renderFormFieldHtml(f, s)).join('\n      ');
  const hiddenHtml = hiddenFields
    .map(f => `<input type="hidden" name="${escapeHtml(f.name)}" value="${escapeHtml(f.defaultValue || '')}">`)
    .join('\n      ');

  return `<amp-story-grid-layer template="fill">
    <div style="${wrapperCss}">
      <form method="post" action-xhr="#" target="_top" style="${formCss}">
        ${titleHtml}
        ${fieldsHtml}
        ${hiddenHtml}
      </form>
    </div>
  </amp-story-grid-layer>`;
}

/**
 * Post-processes AMP HTML from the backend, replacing
 * `<!-- unsupported layer type: form -->` comments with real form HTML.
 * Also injects the amp-form script into <head> when needed.
 */
export function patchAmpFormLayers(ampHtml, story) {
  if (!ampHtml || !story) return ampHtml;

  const hasForm = story.pages.some(p =>
    (p.layers || []).some(l => l.type === 'form')
  );
  if (!hasForm) return ampHtml;

  let result = ampHtml;

  // Inject amp-form extension script after amp-story script
  if (!result.includes('amp-form')) {
    result = result.replace(
      /(<script[^>]+custom-element="amp-story"[^>]*><\/script>)/,
      '$1\n  <script async custom-element="amp-form" src="https://cdn.ampproject.org/v0/amp-form-0.1.js"></script>'
    );
  }

  // Replace unsupported form comments per page
  for (const page of story.pages) {
    const formLayers = (page.layers || []).filter(l => l.type === 'form');
    if (!formLayers.length) continue;

    const pageRe = new RegExp(
      `(<amp-story-page[^>]+id="${page.id}"[^>]*>)([\\s\\S]*?)(?=</amp-story-page>)`,
      'g'
    );

    result = result.replace(pageRe, (_match, open, content) => {
      let idx = 0;
      const patched = content.replace(
        /<!--\s*unsupported layer type:\s*form\s*-->/g,
        () => {
          const layer = formLayers[idx++];
          return layer ? generateFormLayerHtml(layer) : '';
        }
      );
      return open + patched;
    });
  }

  return result;
}
